import React, { useState, useEffect } from "react";
import { Col, Form } from "react-bootstrap";

const AntiemeticTreatmentOption = ({ itemData, onChange, disableForDexamethasone, selectedOptions }) => {
  const [expanded, setExpanded] = useState({}); // Track expanded state for dosageLabels with subDosages
  const [selectedRadios, setSelectedRadios] = useState({}); // Track selected subDosages
  const [disabledOptions, setDisabledOptions] = useState([]); // Track disabled options

  // Set dexamethasone disabled options
  useEffect(() => {
    const dexamethasoneDisabledOptions = [
      "Dexamethasone (choose one)",
      "Dexamethasone 8 mg PO/IV daily on days 2, 3, 4",
      "Dexamethasone 12 mg PO once",
      "Dexamethasone 12 mg IV once",
      "Dexamethasone 12 mg PO/IV once",
      "Dexamethasone 8 mg PO/IV daily on days 2, 3, 4",
      "Dexamethasone 8 mgr,s PO/IV daily on days 2, 3, 4",
      "Dexamethasone 12 mg PO/IV onceu,v",
      "Dexamethasone 8 mgu,v PO/IV daily on days 2, 3, 4",
    ];

    if (disableForDexamethasone) {
      setDisabledOptions((prev) => [...new Set([...prev, ...dexamethasoneDisabledOptions])]);
    } else {
      setDisabledOptions((prev) => prev.filter((opt) => !dexamethasoneDisabledOptions.includes(opt)));
    }
  }, [disableForDexamethasone]);

  const isOptionDisabled = (dosageLabel) => {
    // Ensure disabledOptions is an array before calling includes
    return Array.isArray(disabledOptions) && disabledOptions.includes(dosageLabel);
  };

  // Handles checkbox state changes and expands subDosages if needed
  const handleCheckboxChange = (dosageLabel, hasSubDosages, daysLabel, isCurrentlyChecked) => {
    if (!hasSubDosages) {
      onChange(dosageLabel, daysLabel);
    } else if (isCurrentlyChecked) {
      // Unchecking: remove all matching entries from parent state (handles stale/multiple values),
      // clear local radio state, and collapse
      const allMatching = (selectedOptions?.[daysLabel] || [])
        .filter(v => v === dosageLabel || v.startsWith(`${dosageLabel} - `));
      allMatching.forEach(v => onChange(v, daysLabel));
      setSelectedRadios(prevState => {
        const updated = { ...prevState };
        delete updated[dosageLabel];
        return updated;
      });
      setExpanded(prevState => ({ ...prevState, [dosageLabel]: false }));
    } else {
      // Checking: expand to show sub-dosage options
      setExpanded(prevState => ({
        ...prevState,
        [dosageLabel]: !prevState[dosageLabel]
      }));
    }
  };

  // Handles radio button (subDosage) changes and passes the dosageLabel with subDosage
  const handleRadioChange = (subItem, dosageLabel, daysLabel) => {
    const previousRadio = selectedRadios[dosageLabel];
    if (previousRadio && previousRadio !== subItem) {
      onChange(`${dosageLabel} - ${previousRadio}`, daysLabel);
    }
    setSelectedRadios({ ...selectedRadios, [dosageLabel]: subItem });
    onChange(`${dosageLabel} - ${subItem}`, daysLabel);
  };

  const renderWithSubDosages = (dosageItem) => {
    const isExpanded = expanded[dosageItem.dosageLabel] || false;
    const isSubDosageSelected = !!selectedRadios[dosageItem.dosageLabel]; // Check if a sub-dosage is selected
    const isDisabled = isOptionDisabled(dosageItem.dosageLabel);

    const isChecked = selectedOptions?.[itemData.daysLabel]?.some(v => v === dosageItem.dosageLabel || v.startsWith(dosageItem.dosageLabel + " - ")) || false;

    return (
      <>
        <Form.Check
          type="checkbox"
          value={dosageItem.dosageLabel}
          checked={isChecked}
          onChange={() => handleCheckboxChange(dosageItem.dosageLabel, dosageItem.subDosagesAvailable === "Y", itemData.daysLabel, isChecked)}
          label={dosageItem.dosageLabel}
          disabled={isDisabled}
        />
        {isExpanded && (
          <>
            <div className="ml-4">Please select one:</div>
            {dosageItem.subDosages.map((subItem, i) => (
              <div key={i}>
                <Form.Check
                  className="ml-4"
                  name={dosageItem.dosageLabel}
                  type="radio"
                  value={subItem}
                  checked={selectedRadios[dosageItem.dosageLabel] === subItem}
                  onChange={() => handleRadioChange(subItem, dosageItem.dosageLabel, itemData.daysLabel)}
                  label={subItem}
                  disabled={isDisabled} // Disable based on Dexamethasone logic
                />
              </div>
            ))}
            {!isSubDosageSelected && (
              <div className="text-danger ml-4">Sub-dosage is required for {dosageItem.dosageLabel}</div>
            )}
          </>
        )}
      </>
    );
  };

  const renderWithoutSubDosages = (dosageItem) => {
    const isDisabled = isOptionDisabled(dosageItem.dosageLabel);
    const isChecked = selectedOptions?.[itemData.daysLabel]?.includes(dosageItem.dosageLabel) || false;

    return (
      <Form.Check
        type="checkbox"
        value={dosageItem.dosageLabel}
        checked={isChecked}
        onChange={() => handleCheckboxChange(dosageItem.dosageLabel, false, itemData.daysLabel)}
        label={dosageItem.dosageLabel}
        disabled={isDisabled}
      />
    );
  };

  return (
    <Col lg="6">
      <table className="table table-striped table-hover text-center">
        <thead className="thead-light">
          <tr>
            <th>{itemData.daysLabel}</th>
          </tr>
        </thead>
      </table>
      {itemData.dosages.map((dosageItem, i) => (
        <div key={i}>
          {dosageItem.subDosagesAvailable === "N"
            ? renderWithoutSubDosages(dosageItem)
            : renderWithSubDosages(dosageItem)}
        </div>
      ))}
    </Col>
  );
};

export default AntiemeticTreatmentOption;
